import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Package, Truck, User, CheckCircle2, ArrowRight, ArrowLeft, Send } from 'lucide-react';

const Quote = () => {
  const [step, setStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    equipment: 'dry-van',
    commodity: '',
    weight: '',
    name: '',
    email: '',
    phone: '',
    company: ''
  });

  const steps = [
    { icon: MapPin, title: 'Origin', subtitle: 'Where is the freight?' },
    { icon: MapPin, title: 'Destination', subtitle: 'Where is it going?' },
    { icon: Truck, title: 'Equipment', subtitle: 'What type of trailer?' },
    { icon: Package, title: 'Commodity', subtitle: 'What are you shipping?' },
    { icon: User, title: 'Contact', subtitle: 'How can we reach you?' },
  ];

  const equipmentTypes = [
    { value: 'dry-van', label: 'Dry Van (53\')', desc: 'Standard enclosed trailer' },
    { value: 'reefer', label: 'Refrigerated', desc: 'Temperature controlled' },
    { value: 'flatbed', label: 'Flatbed', desc: 'Open deck for large items' },
    { value: 'step-deck', label: 'Step Deck', desc: 'Lower deck for tall loads' },
    { value: 'ltl', label: 'LTL', desc: 'Less than truckload' },
  ];

  const handleNext = () => {
    if (step < steps.length - 1) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  const handleSubmit = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setIsComplete(true);
    }, 2000);
  };

  const updateField = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
  };

  if (isComplete) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-20" />
        <motion.div 
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-white p-12 rounded-xl text-center max-w-lg shadow-2xl border border-forest/10"
        >
          <div className="w-20 h-20 bg-forest rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-amber" />
          </div>
          <h2 className="font-display font-black text-3xl text-forest mb-4">Quote Request Received!</h2>
          <p className="font-body text-ink/70 mb-8">
            Our team will review your requirements and get back to you within 2 business hours.
          </p>
          <a href="/" className="btn-primary">Return Home</a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone relative overflow-hidden">
      {/* Animated background orbs */}
      <motion.div 
        className="absolute top-20 right-10 w-96 h-96 bg-forest/5 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.2, 1],
          opacity: [0.3, 0.5, 0.3]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-20 left-10 w-80 h-80 bg-amber/10 rounded-full blur-3xl"
        animate={{ 
          scale: [1, 1.3, 1],
          y: [0, -30, 0]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <div className="absolute inset-0 bg-grid opacity-30" />
      
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-32 pb-20 relative z-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="font-mono text-xs tracking-widest uppercase text-forest-2 mb-4"
            >
              Freight Quote Request
            </motion.div>
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="font-display font-black text-display-lg text-forest mb-4"
            >
              Get Your Quote
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="font-body text-ink/70"
            >
              Complete the steps below for your custom freight quote
            </motion.p>
          </div>

          {/* Progress Bar */}
          <div className="mb-12">
            <div className="flex justify-between mb-4">
              {steps.map((s, i) => (
                <div key={i} className={`flex flex-col items-center ${i <= step ? 'text-forest' : 'text-forest/30'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center mb-2 ${i <= step ? 'bg-amber text-forest' : 'bg-forest/20'}`}>
                    <s.icon className="w-5 h-5" />
                  </div>
                  <span className="font-mono text-xs hidden sm:block">{s.title}</span>
                </div>
              ))}
            </div>
            <div className="h-2 bg-forest/20 rounded-full">
              <motion.div 
                className="h-full bg-amber rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((step + 1) / steps.length) * 100}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
          </div>

          {/* Form Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white p-8 lg:p-12 rounded-2xl shadow-xl border border-forest/10"
            >
              <div className="flex items-center gap-4 mb-8">
                <div className="w-14 h-14 bg-amber rounded-xl flex items-center justify-center">
                  {React.createElement(steps[step].icon, { className: 'w-7 h-7 text-forest' })}
                </div>
                <div>
                  <h2 className="font-display font-bold text-2xl text-forest">{steps[step].title}</h2>
                  <p className="font-body text-ink/60">{steps[step].subtitle}</p>
                </div>
              </div>

              {/* Step Content */}
              {step === 0 && (
                <div className="bg-forest p-6 rounded-xl space-y-4">
                  <label className="block font-body text-white/80 mb-2">Origin City/Zip</label>
                  <input 
                    type="text" 
                    value={formData.origin}
                    onChange={(e) => updateField('origin', e.target.value)}
                    placeholder="e.g., Toronto, ON M5V 3A8"
                    className="w-full bg-forest-2 border border-forest-3 rounded-xl px-4 py-4 text-white placeholder-white/40 focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4">
                  <label className="block font-body text-ink/80 mb-2">Destination City/Zip</label>
                  <input 
                    type="text" 
                    value={formData.destination}
                    onChange={(e) => updateField('destination', e.target.value)}
                    placeholder="e.g., Chicago, IL 60601"
                    className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest placeholder-forest/40 focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                  />
                </div>
              )}

              {step === 2 && (
                <div className="space-y-3">
                  {equipmentTypes.map((eq) => (
                    <button
                      key={eq.value}
                      onClick={() => updateField('equipment', eq.value)}
                      className={`w-full p-4 rounded-xl text-left transition-all border-2 ${
                        formData.equipment === eq.value 
                          ? 'bg-amber text-forest border-amber' 
                          : 'bg-white text-forest border-forest/10 hover:border-amber'
                      }`}
                    >
                      <div className="font-display font-bold">{eq.label}</div>
                      <div className={`font-body text-sm ${formData.equipment === eq.value ? 'text-forest/70' : 'text-ink/60'}`}>
                        {eq.desc}
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4">
                  <div>
                    <label className="block font-body text-ink/80 mb-2">Commodity Description</label>
                    <input 
                      type="text" 
                      value={formData.commodity}
                      onChange={(e) => updateField('commodity', e.target.value)}
                      placeholder="e.g., Electronics, Raw Materials, etc."
                      className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest placeholder-forest/40 focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block font-body text-ink/80 mb-2">Weight (lbs)</label>
                    <input 
                      type="number" 
                      value={formData.weight}
                      onChange={(e) => updateField('weight', e.target.value)}
                      placeholder="e.g., 25000"
                      className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest placeholder-forest/40 focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                    />
                  </div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body text-ink/80 mb-2">Name</label>
                      <input 
                        type="text" 
                        value={formData.name}
                        onChange={(e) => updateField('name', e.target.value)}
                        className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block font-body text-ink/80 mb-2">Company</label>
                      <input 
                        type="text" 
                        value={formData.company}
                        onChange={(e) => updateField('company', e.target.value)}
                        className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block font-body text-ink/80 mb-2">Email</label>
                      <input 
                        type="email" 
                        value={formData.email}
                        onChange={(e) => updateField('email', e.target.value)}
                        className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                      />
                    </div>
                    <div>
                      <label className="block font-body text-ink/80 mb-2">Phone</label>
                      <input 
                        type="tel" 
                        value={formData.phone}
                        onChange={(e) => updateField('phone', e.target.value)}
                        className="w-full bg-bone border border-forest/20 rounded-xl px-4 py-4 text-forest focus:outline-none focus:border-amber focus:ring-2 focus:ring-amber/20 transition-all"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-8 border-t border-forest/10">
                <button 
                  onClick={handleBack}
                  disabled={step === 0}
                  className={`flex items-center gap-2 font-body font-semibold ${step === 0 ? 'text-forest/30' : 'text-forest hover:text-amber'}`}
                >
                  <ArrowLeft className="w-5 h-5" /> Back
                </button>
                
                {step < steps.length - 1 ? (
                  <button 
                    onClick={handleNext}
                    className="btn-primary"
                  >
                    Next <ArrowRight className="w-5 h-5" />
                  </button>
                ) : (
                  <button 
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="btn-primary"
                  >
                    {isSubmitting ? 'Sending...' : 'Submit Request'}
                    <Send className="w-5 h-5" />
                  </button>
                )}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default Quote;
